import type { Command } from '../commands.js';
import { type World } from '../world.js';
import type { TowerSpec } from '../scenario.js';

/**
 * The reference player (TRD-13 §7.2).
 *
 * A scripted, deliberately MEDIOCRE policy used for two jobs:
 *
 *  - the solvability probe, which refuses to accept a generated daily this player cannot
 *    carry to the tuned bar (R-13-34);
 *  - the power-curve measurement that balance changes are argued from.
 *
 * Both need the same definition of "playable", so there is exactly one policy and both
 * import it.
 *
 * ── Why mediocre on purpose ───────────────────────────────────────────────────────────
 * It never rerolls, never sells, never saves for a tier-3, and only notices synergies it
 * has already stumbled into. Tuning the probe to a *good* player would certify days that
 * only experts can finish. If a weak player gets through, a real one has a real run.
 *
 * The one thing it does do well is placement: it picks the tile covering the most path,
 * because a policy that places badly measures the map's forgiveness rather than its
 * difficulty. An earlier version placed on the first legal tile near any waypoint and
 * died around wave 5 on maps a human cleared comfortably — which made every number it
 * produced useless.
 */

export interface ReferenceResult {
  waveReached: number;
  coreHp: number;
  towersPlaced: number;
  /** True if the run ended by clearing the target rather than by losing the Core. */
  survived: boolean;
  ticks: number;
}

const MAX_TICKS_PER_WAVE = 60 * 150;

/** Tiles the path runs through, from the build mask. */
function pathTiles(buildMask: readonly string[]): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let y = 0; y < buildMask.length; y++) {
    const row = buildMask[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x++) {
      const c = row[x];
      if (c === 'p' || c === 's' || c === 'c') out.push({ x, y });
    }
  }
  return out;
}

/**
 * How much path a tower on this tile would cover.
 *
 * This is the whole of the policy's skill. Squared distance against squared range keeps
 * it out of `sqrt` — it runs for every free tile on every placement.
 */
function coverageAt(
  tileX: number,
  tileY: number,
  range: number,
  path: readonly { x: number; y: number }[],
): number {
  const cx = tileX + 0.5;
  const cy = tileY + 0.5;
  const r2 = range * range;
  let n = 0;
  for (const tile of path) {
    const dx = tile.x + 0.5 - cx;
    const dy = tile.y + 0.5 - cy;
    if (dx * dx + dy * dy <= r2) n++;
  }
  return n;
}

function bestTile(
  world: World,
  range: number,
  path: readonly { x: number; y: number }[],
): { x: number; y: number } | null {
  const mask = world.config.buildMask;
  const taken = new Set<number>();
  for (const t of world.towers.items) {
    if (t.alive) taken.add(t.tileY * 1000 + t.tileX);
  }

  let best: { x: number; y: number } | null = null;
  let bestScore = -1;

  for (let y = 0; y < mask.length; y++) {
    const row = mask[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== '.') continue;
      if (taken.has(y * 1000 + x)) continue;
      const score = coverageAt(x, y, range, path);
      // Ties break on the lower tile index, so the policy is a pure function of state.
      if (score > bestScore) {
        bestScore = score;
        best = { x, y };
      }
    }
  }
  return bestScore > 0 ? best : null;
}

function rangeOf(world: World, defId: string): number {
  const spec: TowerSpec | undefined = world.config.towerPool.find((t) => t.id === defId);
  return spec?.base.range ?? 4;
}

/** One build phase, then start the wave. */
function playBuildPhase(world: World, path: readonly { x: number; y: number }[]): void {
  const step = (cmd: Command): void => {
    world.step([cmd]);
  };

  // 1. Buy affordable offers, preferring ones that build on a class already on the board.
  //
  //    R-13-36 originally said the reference player ignores synergies. That was written
  //    before it was clear the class web IS the power curve — a policy that ignores it
  //    entirely is not a weak player, it is playing a different game, and it measured the
  //    map's forgiveness rather than its difficulty.
  //
  //    So it does the least a novice would: notices it already has two Frost and takes a
  //    third. It still never rerolls toward a class it needs, never saves for a tier-3,
  //    and never sells a tower that stopped earning its tile.
  for (let guard = 0; guard < 32; guard++) {
    if (world.economy.bench.every((b) => b !== null)) break;

    const counts = new Map<string, number>();
    for (const status of world.snapshotForUI().synergies) counts.set(status.classId, status.count);

    let bestSlot = -1;
    let bestScore = -Infinity;
    for (let slot = 0; slot < world.economy.shop.length; slot++) {
      const offer = world.economy.shop[slot];
      if (!offer || offer.cost > world.economy.gold) continue;

      const spec = world.config.towerPool.find((t) => t.id === offer.defId);
      // Affinity: how much of this tower's classes the board already runs. Cost breaks
      // ties downward, so it still leans cheap.
      const affinity = (spec?.classes ?? []).reduce((sum, c) => sum + (counts.get(c) ?? 0), 0);
      const score = affinity * 10 - offer.cost;
      if (score > bestScore) {
        bestScore = score;
        bestSlot = slot;
      }
    }
    if (bestSlot < 0) break;

    const before = world.economy.gold;
    step({ t: 'buy', slot: bestSlot });
    if (world.economy.gold === before) break; // rejected — stop rather than spin
  }

  // 2. Merge whatever is ready. Since placed towers no longer merge on their own, a
  //    player who never merged would never see a star above 1 — that is not mediocre,
  //    that is broken.
  for (const tower of world.snapshotForUI().placedTowers) {
    if (tower.mergeReady) step({ t: 'merge', tower: tower.id });
  }

  // 3. Place onto the highest-coverage free tile.
  while (world.economy.placementsLeft > 0) {
    const benchIndex = world.economy.bench.findIndex((item) => item !== null);
    if (benchIndex < 0) break;

    const item = world.economy.bench[benchIndex];
    if (!item) break;

    const tile = bestTile(world, rangeOf(world, item.defId), path);
    if (!tile) break;

    const before = world.placedTowerCount();
    step({ t: 'place', benchIndex, tileX: tile.x, tileY: tile.y });
    // Board cap reached or placement refused — either way stop.
    if (world.placedTowerCount() === before) break;
  }

  step({ t: 'startWave' });
}

/**
 * Play a world to `targetWave` or to death.
 *
 * Deterministic: every decision is a pure function of world state, so the same seed and
 * config always produce the same result. That is what lets the probe's verdict be part of
 * the daily's identity (R-13-37).
 */
export function playReferenceRun(world: World, targetWave: number): ReferenceResult {
  const path = pathTiles(world.config.buildMask);
  let guard = 0;

  while (world.phase !== 'ended' && world.wave < targetWave && guard++ < targetWave + 5) {
    playBuildPhase(world, path);

    let ticks = 0;
    while (world.phase === 'combat' && ticks++ < MAX_TICKS_PER_WAVE) world.step([]);
    if (ticks >= MAX_TICKS_PER_WAVE) break; // stalled wave — treat as a failure
  }

  return {
    waveReached: world.coreHp > 0 ? world.wave : Math.max(0, world.wave - 1),
    coreHp: world.coreHp,
    towersPlaced: world.placedTowerCount(),
    survived: world.coreHp > 0,
    ticks: world.tick,
  };
}
